import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import IconButton from '@mui/material/IconButton';

import { AssistancesStyles } from '../assistances/assistances-style';
import { OnboardingStyle } from '../../public/onboarding/onboarding-style';
import { CoverageFilterValidator } from '../../../services/coverages/coverage.filter';
import { AuthHeader, ContentLayout } from '../../../global-styles/layout';
import { BodyText, Heading2Text } from '../../../components/typography/typography';
import { icons } from '../../../assets/icons/icons';
import { getPolicyCoverages } from '../../../api/policy/policy';
import { CoveragesParams } from './coverages.types';
import CoverageItem from './components/CoverageItem';

const Coverages = () => {
  const { t } = useTranslation(['common', 'completed']);
  const { file, intervention } = useParams<CoveragesParams>();
  const navigate = useNavigate();
  const coverageFilter = new CoverageFilterValidator();

  const {
    data: coverages = [],
    isPending,
    isError,
  } = useQuery({
    queryKey: ['coverages', file],
    queryFn: async () => {
      let result = await getPolicyCoverages(intervention!);
      result = result.filter((coverage) => coverageFilter.match(coverage) > 0);
      result = result.map((coverage) => coverageFilter.removeParentheses(coverage));
      return result;
    },
    enabled: Boolean(file),
  });

  useEffect(() => {
    if (!file || !intervention) {
      navigate('/invalid-data', { replace: true });
    }
  }, [file, intervention, navigate]);

  const handleReturn = () => {
    navigate(`/completed/${file}/${intervention}`);
  };

  return (
    <ContentLayout>
      <AssistancesStyles>
        <OnboardingStyle>
          <div className="onboarding-top">
            <AuthHeader>
              <div className="auth-header-title assistances-title">
                <IconButton className="assistances-title__back" aria-label="back" onClick={handleReturn}>
                  <img src={icons.arrowLeftIcon} alt="" />
                </IconButton>
                <Heading2Text as="h1" weight={600}>
                  {t('completed:coverages.title')}
                </Heading2Text>
              </div>
              <div className="assistances-header-divider">
                <BodyText className="assistances-subtitle">{t('completed:coverages.subtitle')}</BodyText>
              </div>
            </AuthHeader>
            {isError ? (
              <BodyText className="assistances-empty" role="alert">
                {t('completed:coverages.error')}
              </BodyText>
            ) : coverages.length > 0 ? (
              <ul className="assistances-list">
                {coverages.map((coverage, index) => (
                  <li key={`${index}`}>
                    <CoverageItem coverage={coverage} />
                  </li>
                ))}
              </ul>
            ) : (
              !isPending && <BodyText className="assistances-empty">{t('completed:coverages.empty')}</BodyText>
            )}
          </div>
        </OnboardingStyle>
      </AssistancesStyles>
    </ContentLayout>
  );
};

export default Coverages;
